import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { LiveEmbedState } from './liveEmbedProtocol'

const DOT_COLORS: Record<LiveEmbedState['status'], string> = {
  loading: '#9e9e9e',
  online: '#2e7d32',
  offline: '#d32f2f',
  unavailable: '#757575',
}

// `tinted`: pale shade of the dot's colour as background, dark text of the same hue (mini preview).
const TINTED: Record<LiveEmbedState['status'], { background: string; text: string }> = {
  loading: { background: '#f5f5f5', text: '#424242' },
  online: { background: '#e8f5e9', text: '#1b5e20' },
  offline: { background: '#ffebee', text: '#b71c1c' },
  unavailable: { background: '#eeeeee', text: '#616161' },
}

const DARK = { background: 'rgba(0, 0, 0, 0.7)', text: '#ffffff' }

const DAY_MS = 24 * 60 * 60 * 1000

/** "14:02", or "3 oct. 14:02" when it is more than 24 h old so the time alone is not mistaken for today's. */
export const formatSince = (ms: number, now: number = Date.now()) =>
  now - ms > DAY_MS
    ? new Date(ms).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

interface LiveStatusBadgeProps {
  state: LiveEmbedState
  variant?: 'dark' | 'tinted'
}

/** "● Live" / "● Offline since 14:02" badge of a live dashboard. */
const LiveStatusBadge = ({ state, variant = 'dark' }: LiveStatusBadgeProps) => {
  const { t } = useTranslation()
  const colors = variant === 'tinted' ? TINTED[state.status] : DARK
  const label =
    state.status === 'offline'
      ? state.since
        ? t('dashboards.live.offlineSince', { time: formatSince(state.since) })
        : t('dashboards.live.offline')
      : t(`dashboards.live.${state.status}`)

  return (
    <Box
      role="status"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        px: '6px',
        py: '2px',
        borderRadius: '10px',
        backgroundColor: colors.background,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: DOT_COLORS[state.status] }} />
      <Typography variant="caption" sx={{ color: colors.text, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  )
}

export default LiveStatusBadge
